import { useCallback } from 'react';
import { useAppState, useAppActions } from '../context/AppContext';
import { AppView } from '../types';

/**
 * Custom hook for managing navigation and view transitions
 */
export const useNavigation = () => {
  const state = useAppState();
  const actions = useAppActions();

  const { currentView, selectedHealthProfessional, callRequestData } = state;

  // Navigate to a specific view
  const navigateTo = useCallback((view: AppView) => {
    actions.setView(view);
    actions.clearError();
  }, [actions]);

  // Navigate to landing page
  const goToLanding = useCallback(() => {
    navigateTo('landing');
  }, [navigateTo]);

  // Navigate to directory
  // const goToDirectory = useCallback(() => {
  //   navigateTo('directory');
  // }, [navigateTo]);

  // Navigate to call request (requires selected professional)
  const goToCallRequest = useCallback(() => {
    if (!selectedHealthProfessional) {
      actions.setError('Please select a health professional first');
      return false;
    }
    navigateTo('callRequest');
    return true;
  }, [selectedHealthProfessional, navigateTo, actions]);

  // Navigate to confirmation (requires call request data)
  const goToConfirmation = useCallback(() => {
    if (!callRequestData || !selectedHealthProfessional) {
      actions.setError('No call request data available');
      return false;
    }
    navigateTo('confirmation');
    return true;
  }, [callRequestData, selectedHealthProfessional, navigateTo, actions]);

  // Handle "Consult Now" action from landing - go directly to call request
  const handleBookNow = useCallback(() => {
    // Go directly to call request form - no need to fetch professionals
    // Any doctor can accept the request from the marketplace
    navigateTo('callRequest');
  }, [navigateTo]);

  // Handle back navigation with state preservation
  const goBack = useCallback(() => {
    switch (currentView) {
      case 'callRequest':
        // Go back to landing (no directory step)
        goToLanding();
        // Clear selected professional since it was auto-selected
        actions.setSelectedProfessional(null);
        break;
      case 'confirmation':
        // From confirmation, go back to landing to start over
        goToLanding();
        actions.setSelectedProfessional(null);
        actions.setCallRequestData(null);
        break;
      default:
        goToLanding();
    }
  }, [currentView, goToLanding, actions]);

  // Check if navigation to a view is allowed
  const canNavigateTo = useCallback((view: AppView): boolean => {
    switch (view) {
      case 'landing':
        return true;
      case 'callRequest':
        return !!selectedHealthProfessional;
      case 'confirmation':
        return !!callRequestData && !!selectedHealthProfessional;
      default:
        return false;
    }
  }, [selectedHealthProfessional, callRequestData]);

  // Get the next logical view in the flow
  const getNextView = useCallback((): AppView | null => {
    switch (currentView) {
      case 'landing':
        return 'callRequest'; // Skip directory, go directly to call request
      case 'callRequest':
        return callRequestData ? 'confirmation' : null;
      case 'confirmation':
        return null; // End of flow
      default:
        return null;
    }
  }, [currentView, callRequestData]);

  // Get the previous view in the flow
  const getPreviousView = useCallback((): AppView | null => {
    switch (currentView) {
      case 'landing':
        return null; // Start of flow
      case 'callRequest':
        return 'landing'; // Skip directory, go back to landing
      case 'confirmation':
        return 'landing'; // Go back to landing to start over
      default:
        return null;
    }
  }, [currentView]);

  // Navigate to next view in flow
  const goToNext = useCallback(() => {
    const nextView = getNextView();
    if (nextView && canNavigateTo(nextView)) {
      navigateTo(nextView);
      return true;
    }
    return false;
  }, [getNextView, canNavigateTo, navigateTo]);

  // Navigate to previous view in flow
  const goToPrevious = useCallback(() => {
    const previousView = getPreviousView();
    if (previousView) {
      navigateTo(previousView);
      return true;
    }
    return false;
  }, [getPreviousView, navigateTo]);

  return {
    // Current state
    currentView,
    
    // Navigation functions
    navigateTo,
    goToLanding,
    goToCallRequest,
    goToConfirmation,
    handleBookNow,
    goBack,
    goToNext,
    goToPrevious,
    
    // Utility functions
    canNavigateTo,
    getNextView,
    getPreviousView,
    
    // Flow state
    isAtStart: currentView === 'landing',
    isAtEnd: currentView === 'confirmation',
    canGoNext: !!getNextView(),
    canGoBack: !!getPreviousView(),
  };
};